import {useCallback} from 'react';
import {Connection} from 'reactflow';
import {v4 as uuidv4} from 'uuid';

import * as analytics from 'analytics/Analytics';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {FlowchartEditorMode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';

import {generateDeterministicEdgeId} from '../buildStaticDataFromFlowchartConfigGraphQL';
import {getEdgeTypeFromHandleId} from '../flowchartIdentifierUtils';
import {
  FlowchartStaticEdgeData,
  ProcessFlowchartEdgeType,
  StaticTemplateFlowchart,
} from '../processFlowchartConfigEditorConstants';
import {isStaticTaskSchemaNodeData} from '../processFlowchartConfigEditorNodeTypes';
import {FlowchartEdgeChangeType} from '../processFlowchartConfigEditorUtils';
import {getDefaultTransitionSource} from '../TransitionSourceUtils';
import {createEmptyTransition} from '../types/transition/transitionKeyUtils';
import {
  addDataFlowFieldPairsToTransition,
  findMergeTransitionCandidates,
  getOptionalTransitionsForNewEdge,
  promptForMergeTransition,
  shouldAddEdges,
} from '../types/transition/transitionLifecycleUtils';
import {MergeTransitionCandidate} from '../types/transition/transitionLifecycleUtils';
import {Transition} from '../types/transition/TransitionTypes';
import {ApplyFlowchartChangesFn} from './FlowchartStateTypes';
import {trackOptionalTransitionPromotedAnalytics} from './useFlowchartStateAnalytics';
import {ShowMergeModalFn} from './useMergeTransitionModal';

/**
 * Attempts to merge a new edge into an existing transition. Returns `true` if
 * the merge was applied (or the user cancelled), meaning the caller should not
 * fall through to the default "add edge" path.
 */
async function tryMergeTransition({
  mergeCandidate,
  newStaticEdge,
  staticFlowchart,
  flowchartTypeName,
  applyFlowchartChanges,
  showMergeModal,
}: {
  mergeCandidate: MergeTransitionCandidate;
  newStaticEdge: FlowchartStaticEdgeData;
  staticFlowchart: StaticTemplateFlowchart;
  flowchartTypeName: string;
  applyFlowchartChanges: ApplyFlowchartChangesFn;
  showMergeModal: ShowMergeModalFn;
}): Promise<boolean> {
  const mergeResult = await promptForMergeTransition({
    candidate: mergeCandidate,
    nodes: staticFlowchart.nodesStaticData,
    flowchartTypeName,
    showMergeModal,
  });

  if (mergeResult == null) {
    return true;
  }

  if (!mergeResult.shouldMerge || mergeResult.selectedTransitionId == null) {
    return false;
  }

  const selectedTransition = mergeCandidate.existingTransitions.find(
    (t) => t.id === mergeResult.selectedTransitionId
  );
  if (selectedTransition == null) {
    // Stale candidate list — treat as "keep separate" so we don't drop the new edge
    return false;
  }

  const allSourcePTDVIds = [...selectedTransition.sourcePTDVIds, mergeCandidate.newSourcePTDVId];
  const mergedTransition = createEmptyTransition({
    sourcePTDVIds: allSourcePTDVIds,
    targetPTDVId: mergeCandidate.targetPTDVId,
    isPlanned: true,
  });

  for (const [sourcePTDVId, source] of Object.entries(selectedTransition.transitionSourcesBySourcePTDVId)) {
    mergedTransition.transitionSourcesBySourcePTDVId[sourcePTDVId] = source;
  }
  for (const [key, value] of Object.entries(selectedTransition.customMappingDataByFieldPair)) {
    mergedTransition.customMappingDataByFieldPair[key] = value;
  }

  const sourceNode = staticFlowchart.nodesStaticData.find(
    (n) =>
      isStaticTaskSchemaNodeData(n) && n.taskDefinitionVersionSpec.clientId === mergeCandidate.newSourcePTDVId
  );
  if (sourceNode != null) {
    mergedTransition.transitionSourcesBySourcePTDVId[mergeCandidate.newSourcePTDVId] =
      getDefaultTransitionSource(sourceNode.id, staticFlowchart.nodesStaticData);
  }

  addDataFlowFieldPairsToTransition(
    mergeCandidate.newSourcePTDVId,
    mergeCandidate.targetPTDVId,
    staticFlowchart.edgesStaticData,
    staticFlowchart.nodesStaticData,
    mergedTransition
  );

  applyFlowchartChanges(() => ({
    edgeChanges: [{type: FlowchartEdgeChangeType.ADD, item: newStaticEdge}],
    transitionChanges: [
      {type: 'remove' as const, id: selectedTransition.id},
      {type: 'add' as const, transition: mergedTransition},
    ],
  }));

  analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.MERGE_TRANSITION_CREATED, {
    actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
    flowchartType: flowchartTypeName,
    numSources: allSourcePTDVIds.length,
  });

  return true;
}

interface UseOnConnectParams {
  applyFlowchartChanges: ApplyFlowchartChangesFn;
  staticFlowchart: StaticTemplateFlowchart;
  transitions: Record<string, Transition>;
  flowchartTypeName: string;
  editorMode: FlowchartEditorMode;
  disallowTransitionEdits: boolean;
  showMergeModal: ShowMergeModalFn;
}

/**
 * Builds the `onConnect` callback fired by ReactFlow when the user draws a new
 * edge. Determines the edge type from source/target handles, gates the add on
 * `shouldAddEdges` (which may prompt for optional-transition promotion), and
 * tracks analytics.
 */
export function useOnConnect({
  applyFlowchartChanges,
  staticFlowchart,
  transitions,
  flowchartTypeName,
  editorMode,
  disallowTransitionEdits,
  showMergeModal,
}: UseOnConnectParams): (connection: Connection) => Promise<void> {
  return useCallback(
    async (connection: Connection): Promise<void> => {
      if (
        connection.source == null ||
        connection.target == null ||
        connection.sourceHandle == null ||
        connection.targetHandle == null
      ) {
        return;
      }
      const {source, target, sourceHandle, targetHandle} = connection;

      const sourceHandleType = getEdgeTypeFromHandleId(sourceHandle);
      const targetHandleType = getEdgeTypeFromHandleId(targetHandle);

      const edgeType =
        sourceHandleType === targetHandleType ? sourceHandleType : ProcessFlowchartEdgeType.CONTROL_FLOW;

      analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.EDGE_DRAWN, {
        actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
        edgeType,
        flowchartType: flowchartTypeName,
        editorMode,
      });

      const newStaticEdge: FlowchartStaticEdgeData = {
        id:
          edgeType === ProcessFlowchartEdgeType.CONTROL_FLOW
            ? generateDeterministicEdgeId(source, target)
            : uuidv4(),
        source,
        target,
        sourceHandle,
        targetHandle,
        type: edgeType,
      };

      if (
        disallowTransitionEdits ||
        (await shouldAddEdges({
          edges: [newStaticEdge],
          staticFlowchart,
          transitions,
          flowchartTypeName,
        }))
      ) {
        const promotedOptionalTransitions = !disallowTransitionEdits
          ? getOptionalTransitionsForNewEdge(
              newStaticEdge,
              staticFlowchart.nodesStaticData,
              [...staticFlowchart.edgesStaticData, newStaticEdge],
              transitions
            )
          : [];

        if (!disallowTransitionEdits) {
          trackOptionalTransitionPromotedAnalytics(promotedOptionalTransitions, flowchartTypeName);
        }

        // Skip merge detection when a merging transition was just promoted —
        // the promoted transition already encompasses the merge.
        const isPromotingMergingTransition = promotedOptionalTransitions.some(
          (t) => t.sourcePTDVIds.length >= 2
        );

        const mergeCandidate =
          !disallowTransitionEdits &&
          !isPromotingMergingTransition &&
          editorMode !== FlowchartEditorMode.SINGLE_TRANSITION_CANVAS
            ? findMergeTransitionCandidates(
                newStaticEdge,
                staticFlowchart.nodesStaticData,
                [...staticFlowchart.edgesStaticData, newStaticEdge],
                transitions
              )
            : null;

        if (mergeCandidate != null) {
          const handled = await tryMergeTransition({
            mergeCandidate,
            newStaticEdge,
            staticFlowchart,
            flowchartTypeName,
            applyFlowchartChanges,
            showMergeModal,
          });
          if (handled) {
            return;
          }
        }

        applyFlowchartChanges(() => ({
          edgeChanges: [{type: FlowchartEdgeChangeType.ADD, item: newStaticEdge}],
        }));
      }
    },
    [
      applyFlowchartChanges,
      disallowTransitionEdits,
      editorMode,
      flowchartTypeName,
      showMergeModal,
      staticFlowchart,
      transitions,
    ]
  );
}
